import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { 
  Configuration, 
  ConfigurationData, 
  ValidationErrors, 
  ConfigurationManifest 
} from '@h2/types';

interface ConfigurationState {
  // 현재 설정 구성
  activeConfiguration: Configuration | null;
  
  // 폼 데이터
  formData: ConfigurationData;
  
  // 검증 오류
  errors: ValidationErrors;
  
  // 표시 모드
  viewMode: 'stepper' | 'scroll' | 'manifest';
  
  // 스테퍼 모드 관련
  currentStep: number;
  completedSteps: number[];
  
  // JSON 매니페스트 모드 관련
  jsonText: string;
  jsonError: string | null;
  
  // 로딩/상태
  isLoading: boolean;
  isDirty: boolean;
  
  // 액션들
  setConfiguration: (config: Configuration) => void;
  setFormData: (formData: any) => void;
  updateField: (path: string, value: any) => void;
  setViewMode: (mode: 'stepper' | 'scroll' | 'manifest') => void;
  setCurrentStep: (step: number) => void;
  markStepCompleted: (step: number) => void;
  validateField: (path: string) => string | null;
  validateSection: (sectionId: string) => boolean;
  validateAll: () => boolean;
  updateJsonText: (text: string) => void;
  formatJson: () => void;
  resetForm: () => void;
  submitConfiguration: () => Promise<void>;
}

export const useConfigurationStore = create<ConfigurationState>()(
  devtools(
    persist(
      (set, get) => ({
        // 초기 상태
        activeConfiguration: null,
        formData: {},
        errors: {},
        viewMode: 'stepper',
        currentStep: 0,
        completedSteps: [],
        jsonText: '{}',
        jsonError: null,
        isLoading: false,
        isDirty: false,

        // 설정 구성 설정
        setConfiguration: (config) => {
          const initialData: ConfigurationData = {};
          
          // 기본값으로 formData 초기화
          config.sections.forEach(section => {
            section.fields.forEach(field => {
              if (field.defaultValue !== undefined) {
                initialData[field.id] = field.defaultValue;
              } else if (field.value !== undefined) {
                initialData[field.id] = field.value;
              }
            });
          });

          set({
            activeConfiguration: config,
            formData: initialData,
            errors: {},
            currentStep: 0,
            completedSteps: [],
            jsonText: JSON.stringify(initialData, null, 2),
            jsonError: null,
            isDirty: false
          });
        },

        // FormData 전체 업데이트 (배치 처리용)
        setFormData: (formData) => {
          console.log('🔄 setFormData 호출:', formData);
          
          set({
            formData: formData,
            jsonText: JSON.stringify(formData, null, 2),
            isDirty: true
          });

          // 모든 validation 새로고침 (다음 tick에서 실행)
          setTimeout(() => {
            const currentState = get();
            console.log('🔄 모든 validation 새로고침 시작');
            currentState.validateAll();
          }, 0);
        },

        // 필드 값 업데이트
        updateField: (path, value) => {
          console.log('🔄 updateField 호출:', { path, value });
          const state = get();
          const newFormData = { ...state.formData };
          
          // 중첩된 경로 지원 (예: 'user.name', 'settings.theme')
          const keys = path.split('.');
          let current = newFormData;
          
          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }
          
          current[keys[keys.length - 1]] = value;
          console.log('✅ updateField 완료:', { path, newFormData });

          set({
            formData: newFormData,
            errors: { ...state.errors },
            jsonText: JSON.stringify(newFormData, null, 2),
            isDirty: true
          });

          // 실시간 검증 (업데이트 후 실행)
          setTimeout(() => {
            const currentState = get();
            const error = currentState.validateField(path);
            const newErrors = { ...currentState.errors };
            
            if (error) {
              newErrors[path] = error;
            } else {
              delete newErrors[path];
            }

            set({ errors: newErrors });
          }, 0);
        },

        // 표시 모드 변경
        setViewMode: (mode) => {
          const state = get();
          if (mode === 'manifest') {
            // manifest 모드로 전환 시: formData를 JSON으로 변환
            set({
              viewMode: mode,
              jsonText: JSON.stringify(state.formData, null, 2)
            });
          } else {
            // 다른 모드로 전환 시: 현재 jsonText가 유효하면 formData로 파싱 적용
            if (state.viewMode === 'manifest' && state.jsonText && !state.jsonError) {
              try {
                const parsedData = JSON.parse(state.jsonText);
                set({
                  viewMode: mode,
                  formData: parsedData,
                  jsonError: null
                });
              } catch (error) {
                // JSON이 유효하지 않으면 mode만 변경
                set({ viewMode: mode });
              }
            } else {
              set({ viewMode: mode });
            }
          }
        },

        // 현재 스텝 설정
        setCurrentStep: (step) => {
          set({ currentStep: step });
        },

        // 스텝 완료 표시
        markStepCompleted: (step) => {
          const state = get();
          const completedSteps = [...state.completedSteps];
          
          if (!completedSteps.includes(step)) {
            completedSteps.push(step);
          }
          
          set({ completedSteps });
        },

        // 필드 검증
        validateField: (path) => {
          const state = get();
          const config = state.activeConfiguration;
          
          if (!config) return null;

          // 필드 찾기
          let field = null;
          for (const section of config.sections) {
            field = section.fields.find(f => f.id === path);
            if (field) break;
          }

          if (!field || !field.validation) return null;

          const value = state.formData[path];
          console.log('🔍 validateField:', { path, value, field: field.id });

          // 검증 규칙 확인
          for (const rule of field.validation.rules || field.validation) {
            switch (rule.type) {
              case 'required':
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                  return rule.message || '필수 입력 항목입니다';
                }
                break;
              
              case 'custom':
                if (rule.validator && typeof rule.validator === 'function') {
                  const isValid = rule.validator(value);
                  console.log('🎯 custom validation:', { path, value, isValid, message: rule.message });
                  if (!isValid) {
                    return rule.message || '유효하지 않은 값입니다';
                  }
                }
                break;
              
              case 'minLength':
                if (typeof value === 'string' && value.length < rule.value) {
                  return rule.message || `최소 ${rule.value}글자 이상 입력해주세요`;
                }
                break;
              
              case 'maxLength':
                if (typeof value === 'string' && value.length > rule.value) {
                  return rule.message || `최대 ${rule.value}글자까지 입력 가능합니다`;
                }
                break;
              
              case 'min':
                if (typeof value === 'number' && value < rule.value) {
                  return rule.message || `최소값은 ${rule.value}입니다`;
                }
                break;
              
              case 'max':
                if (typeof value === 'number' && value > rule.value) {
                  return rule.message || `최대값은 ${rule.value}입니다`;
                }
                break;
            }
          }

          return null;
        },

        // 섹션 검증
        validateSection: (sectionId) => {
          const state = get();
          const config = state.activeConfiguration;
          
          if (!config) return false;

          const section = config.sections.find(s => s.id === sectionId);
          if (!section) return false;

          // 섹션의 모든 필드 검증
          for (const field of section.fields) {
            // 의존성 확인
            if (field.dependency) {
              const dependentValue = state.formData[field.dependency.field];
              if (dependentValue !== field.dependency.value) {
                continue; // 의존성이 충족되지 않으면 검증 스킵
              }
            }

            const error = state.validateField(field.id);
            if (error) {
              return false;
            }
          }

          return true;
        },

        // 전체 검증
        validateAll: () => {
          const state = get();
          const config = state.activeConfiguration;
          
          if (!config) return false;

          const newErrors: ValidationErrors = {};

          // 모든 필드 검증
          for (const section of config.sections) {
            for (const field of section.fields) {
              // 의존성 확인
              if (field.dependency) {
                const dependentValue = state.formData[field.dependency.field];
                if (dependentValue !== field.dependency.value) {
                  continue; // 의존성이 충족되지 않으면 검증 스킵
                }
              }

              const error = state.validateField(field.id);
              if (error) {
                newErrors[field.id] = error;
              }
            }
          }

          set({ errors: newErrors });
          return Object.keys(newErrors).length === 0;
        },

        // JSON 텍스트 업데이트
        updateJsonText: (text) => {
          try {
            const parsed = JSON.parse(text);
            // 유효한 JSON인 경우: formData 업데이트
            set({
              jsonText: text,
              formData: parsed,
              jsonError: null,
              isDirty: true
            });
          } catch (error) {
            // 유효하지 않은 JSON인 경우: jsonText만 업데이트 (실시간 편집 허용)
            set({
              jsonText: text,
              jsonError: error instanceof Error ? error.message : '유효하지 않은 JSON 형식입니다'
            });
          }
        },

        // JSON 포맷팅
        formatJson: () => {
          const state = get();
          try {
            const parsed = JSON.parse(state.jsonText);
            const formatted = JSON.stringify(parsed, null, 2);
            set({
              jsonText: formatted,
              formData: parsed, // 포맷팅하면서 formData도 업데이트
              jsonError: null,
              isDirty: true
            });
          } catch (error) {
            set({
              jsonError: error instanceof Error ? error.message : 'JSON을 포맷할 수 없습니다'
            });
          }
        },

        // 폼 리셋
        resetForm: () => {
          const state = get();
          const config = state.activeConfiguration;
          
          if (!config) return;

          const initialData: ConfigurationData = {};
          
          // 기본값으로 초기화
          config.sections.forEach(section => {
            section.fields.forEach(field => {
              if (field.defaultValue !== undefined) {
                initialData[field.id] = field.defaultValue;
              }
            });
          });

          set({
            formData: initialData,
            errors: {},
            currentStep: 0,
            completedSteps: [],
            jsonText: JSON.stringify(initialData, null, 2),
            jsonError: null,
            isDirty: false
          });
        },

        // 설정 제출
        submitConfiguration: async () => {
          const state = get();
          
          if (!state.validateAll()) {
            throw new Error('검증 오류가 있습니다');
          }

          set({ isLoading: true });

          try {
            // 여기서 실제 API 호출
            console.log('설정 제출:', state.formData);
            
            // 시뮬레이션된 지연
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            set({ 
              isLoading: false, 
              isDirty: false 
            });
          } catch (error) {
            set({ isLoading: false });
            throw error;
          }
        }
      }),
      {
        name: 'configuration-store',
        partialize: (state) => ({
          // 지속적으로 저장할 상태만 선택
          formData: state.formData,
          viewMode: state.viewMode,
          currentStep: state.currentStep,
          completedSteps: state.completedSteps
        })
      }
    ),
    { name: 'ConfigurationStore' }
  )
);
